<template>
  <InputMinCharsHintWrapper
    :input-value="inputValue"
    :is-hint-shown="isHintShown"
    :min-chars="SEARCH_MIN_CHARS"
    @focusin="setIsHintShown(true)"
    @focusout="setIsHintShown(false)"
  >
    <LocationAutocomplete
      v-model="proxyModelValue"
      :search-min-chars="SEARCH_MIN_CHARS"
      :search-performer="locationSearchDataClient"
      label="Filter postcode"
      @on-select-item="setLocation"
      @update:model-value="setInputValue"
    />
  </InputMinCharsHintWrapper>
</template>

<script setup lang="ts">
  // Types
  import type { WritableComputedRef } from 'vue';
  import type { ILocationSearchResult, TSearchPerformer } from '~/misc/location/search/types';
  import type { TListingFilterLocation } from '~/misc/listings/types';
  import { EAnalyticsEvent } from '~/misc/services/analytics/types';

  // Services
  import { LocationSearchDataClient } from '~/misc/location/search/services/location-search-data-client';

  // Config
  import { INCLUDE_SEARCH, SEARCH_MIN_CHARS } from '~/misc/listings/location-search/config';

  // Components
  import LocationAutocomplete from '~/misc/location/autocomplete';
  import InputMinCharsHintWrapper from '~/misc/input/min-chars-hint-wrapper/InputMinCharsHintWrapper.vue';

  // Utils
  import { eventBus } from '~/misc/utils/event-bus';

  const props = defineProps({
    modelValue: {
      type: Object as PropType<TListingFilterLocation>,
      default: undefined,
    },
  });

  const proxyModelValue: WritableComputedRef<string> = computed({
    get(): string {
      return props.modelValue?.value || '';
    },
    set(value: string): void {
      if (!value) {
        emit('update:modelValue', undefined);
        eventBus.emit(EAnalyticsEvent.DASHBOARD_POSTCODE_FILTER_RESET);
      }
    },
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: TListingFilterLocation | undefined): void;
  }>();

  const locationSearchDataClient: TSearchPerformer = new LocationSearchDataClient(INCLUDE_SEARCH);

  const inputValue: Ref<string> = ref<string>('');
  const isHintShown: Ref<boolean> = ref<boolean>(false);

  const setInputValue = (value: string) => {
    inputValue.value = value;
  };

  const setIsHintShown = (state: boolean) => {
    isHintShown.value = state;
  };

  const setLocation = (location: ILocationSearchResult) => {
    emit('update:modelValue', {
      type: location.type,
      value: location.full_address,
    });

    eventBus.emit(EAnalyticsEvent.DASHBOARD_POSTCODE_FILTER_APPLIED, {
      value: location.full_address,
    });
  };
</script>
